import { Inject, Injectable, InjectionToken } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { from, Observable } from 'rxjs';
import { filter, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { FormSignatureService } from '@protocols/domain';
import type { CaseDataField } from '@protocols/domain';
import type { IRetrieveCaseTypeQuery, PipelineStepDraftDto } from '@protocols/use-cases';

import { AssignedMethodTypesState } from '../state/assigned-method-types.state';
import { CaseTypeEditorState } from '../state/case-type-editor.state';
import type { CaseTypeDraft } from '../state/case-type-editor.state';
import { LabelConfigState } from '../state/label-config.state';
import type { StagedLabelConfig } from '../state/label-config.state';
import { LabelState } from '../state/label.state';
import type { LabelData, LabelOverride } from '../state/label.state';
import type { MethodTypeItem } from '../state/method-type-list.state';
import { StagedCaseDataState } from '../state/staged-case-data.state';
import { StagedMethodTypesState } from '../state/staged-method-types.state';
import type { StagedMethodTypeDraft, StagedMethodTypeOverride } from '../state/staged-method-types.state';
import { StagedPipelinesState } from '../state/staged-pipelines.state';
import type { StagedPipelineDraft, StagedPipelineOverride } from '../state/staged-pipelines.state';
import { StagedResourcesState } from '../state/staged-resources.state';
import type { ResourceDraftData, ResourceDraftOverride } from '../state/staged-resources.state';

export const RETRIEVE_CASE_TYPE_QUERY = new InjectionToken<IRetrieveCaseTypeQuery>('RETRIEVE_CASE_TYPE_QUERY');

/**
 * The case type an editor session operates on: its id plus whether it is
 * already persisted. A not-yet-persisted (create) session carries no id —
 * there is nothing in the DB to fetch, and the case-type id (unlike the
 * method-type's) is not remapped into any child context.
 */
export interface CaseTypeEditorContext {
  caseTypeId: string | undefined;
  persisted: boolean;
}

/**
 * The mandatory, non-removable case-name field every case type carries. A
 * brand-new case type seeds it so the manager always has it to render (the
 * manager pins it as non-removable; old-editor rule).
 */
const DEFAULT_CASE_NAME_FIELD: CaseDataField = {
  type: 'case_name',
  name: 'case_identifier',
  displayName: 'Case Name',
  required: true,
  encrypted: false,
};

@Injectable()
export class CaseTypeEditorEffects {
  constructor(
    private readonly state: CaseTypeEditorState,
    private readonly stagedCaseData: StagedCaseDataState,
    private readonly assignedMethodTypes: AssignedMethodTypesState,
    @Inject(RETRIEVE_CASE_TYPE_QUERY)
    private readonly retrieveCaseTypeQuery: IRetrieveCaseTypeQuery,
    private readonly stagedMethodTypes: StagedMethodTypesState,
    private readonly stagedPipelinesState: StagedPipelinesState,
    private readonly stagedResourcesState: StagedResourcesState,
    private readonly labelState: LabelState,
    private readonly labelConfigState: LabelConfigState
  ) {}

  /**
   * Live canonical signature of the editing session. Single source for both
   * the facade's `hasChanges$` compare and `captureBaseline`'s snapshot.
   *
   * NOTE: declared above `captureBaseline` on purpose — its initializer reads
   * this property at construction time.
   */
  public readonly currentSignature$: Observable<string> = this.state.select(
    this.state.draft$,
    this.stagedCaseData.fields$,
    this.assignedMethodTypes.stagedAssigned$,
    this.assignedMethodTypes.stagedUnassignedIds$,
    this.stagedMethodTypes.pendingMethodTypes$,
    this.stagedMethodTypes.methodTypeOverrides$,
    this.stagedPipelinesState.pendingPipelines$,
    this.stagedPipelinesState.pipelineOverrides$,
    this.stagedResourcesState.pendingResources$,
    this.stagedResourcesState.resourceOverrides$,
    this.labelState.pendingLabels$,
    this.labelState.labelOverrides$,
    this.labelConfigState.stagedConfigs$,
    (
      draft,
      fields,
      stagedAssigned,
      stagedUnassignedIds,
      pendingMethodTypes,
      methodTypeOverrides,
      pendingPipelines,
      pipelineOverrides,
      pendingResources,
      resourceOverrides,
      pendingLabels,
      labelOverrides,
      stagedConfigs
    ) =>
      FormSignatureService.compute(
        this.projectSignature({
          draft,
          fields,
          stagedAssigned,
          stagedUnassignedIds,
          pendingMethodTypes,
          methodTypeOverrides,
          pendingPipelines,
          pipelineOverrides,
          pendingResources,
          resourceOverrides,
          pendingLabels,
          labelOverrides,
          stagedConfigs,
        })
      )
  );

  /**
   * Snapshots the session signature into `baselineSignature` so the facade's
   * `hasChanges$` can detect divergence afterwards. Triggered at both
   * seed-completion points; no self-gating needed — every input is either
   * seeded synchronously in the seed path (editor row, data fields, assigned
   * deltas) or starts empty and only moves on user action (staging ledgers).
   * The load-error path never triggers it: baseline stays `null`,
   * `hasChanges$` stays false, cancel closes silently (correct — nothing was
   * seeded).
   */
  private readonly captureBaseline = this.state.effect<void>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.currentSignature$),
      tap(([, signature]) => this.state.setBaselineSignature(signature))
    )
  );

  public readonly loadCaseType = this.state.effect((context$: Observable<CaseTypeEditorContext>) =>
    context$.pipe(
      tap((context) => {
        if (!context.persisted) {
          // Create mode: pristine row + the mandatory case-name field, and an
          // empty staged-assigned set (a brand-new case type has no persisted
          // method-type associations to seed).
          this.state.seedForCreate();
          this.stagedCaseData.setFields([DEFAULT_CASE_NAME_FIELD]);
          // Nothing is persisted yet: no field name is locked, so every name
          // tracks its display name until the first save.
          this.stagedCaseData.setLockedNames([]);
          this.assignedMethodTypes.seed({
            context: { caseTypeId: context.caseTypeId, persisted: false },
            assigned: [],
          });
          // Baseline bakes in the mandatory case-name field — it is not dirt.
          this.captureBaseline();
        } else {
          this.state.setLoading(true);
          this.state.setLoadException(null);
        }
      }),
      filter(
        (context): context is CaseTypeEditorContext & { caseTypeId: string } =>
          context.persisted && context.caseTypeId !== undefined
      ),
      switchMap((context) =>
        from(this.retrieveCaseTypeQuery.execute(context.caseTypeId)).pipe(
          tapResponse(
            (output) => {
              this.state.seedFromCaseType(output);
              this.stagedCaseData.setFields(output.fields);
              // These fields are persisted: freeze their internal names so
              // renaming a display name can't break existing cases.
              this.stagedCaseData.setLockedNames(output.fields.map((field) => field.name));
              this.assignedMethodTypes.seed({
                context: { caseTypeId: context.caseTypeId, persisted: true },
                assigned: output.methodTypes.map((methodType) => ({ id: methodType.id, name: methodType.name })),
              });
              this.captureBaseline();
            },
            (error: Error) => this.state.setLoadException(error)
          )
        )
      )
    )
  );

  // ============================================================================
  // Signature projection
  // ============================================================================

  /*
   * Canonical fingerprint of everything a case-type editing session can
   * change: the row draft, the staged data fields, the session's
   * assigned-method-type deltas, the staged method-type/pipeline/resource/label
   * ledgers and the label-config staging. Compared against a baseline captured
   * at seed to gate the discard-confirmation warning. Serialized by
   * `FormSignatureService.compute` (recursive key sort), so key-insertion
   * order never flips the signature.
   *
   * Deliberate scope choices:
   *  - `draft.id` is excluded — identity, not work (and `markPersisted` swaps it
   *    mid-save).
   *  - `fields` hash as-is, ORDER SIGNIFICANT — the manager reorders.
   *  - Only the stagedAssigned KEYS are hashed (`updateAssigned` value-patches
   *    always accompany a stage op that is already hashed);
   *    `persistedAssigned`/`persistedLoaded` are excluded — deltas alone are
   *    gate-free. The state is single-slice and seeded synchronously on both
   *    load branches, so there is no pointer hazard.
   *  - Resource ledger rows hash in FULL (no subtree filter): the ledger is
   *    page-scoped and only gains rows from user work.
   *  - `AssignedResourcesState.containers` is deliberately NOT hashed: the
   *    nested method-type dialog seeds the container tree on mere open, which
   *    would false-dirty the case type. Accepted miss: assigning an EXISTING
   *    resource inside a nested dialog is invisible here — but saving that
   *    inner dialog always stages a `stagedMethodTypes` row, flipping the case
   *    type dirty anyway, and discarding the inner dialog discards the
   *    assignment. No lost-work path.
   *  - `saving`/`saveExceptions` never participate.
   */
  private projectSignature(input: {
    draft: CaseTypeDraft;
    fields: CaseDataField[];
    stagedAssigned: Record<string, MethodTypeItem>;
    stagedUnassignedIds: string[];
    pendingMethodTypes: StagedMethodTypeDraft[];
    methodTypeOverrides: Record<string, StagedMethodTypeOverride>;
    pendingPipelines: StagedPipelineDraft[];
    pipelineOverrides: Record<string, StagedPipelineOverride>;
    pendingResources: ResourceDraftData[];
    resourceOverrides: Record<string, ResourceDraftOverride>;
    pendingLabels: LabelData[];
    labelOverrides: Record<string, LabelOverride>;
    stagedConfigs: StagedLabelConfig[];
  }): unknown {
    const row = { name: input.draft.name, description: input.draft.description };

    const assignedMethodTypeDeltas = {
      stagedAssignedIds: Object.keys(input.stagedAssigned).sort(),
      stagedUnassignedIds: [...input.stagedUnassignedIds].sort(),
    };

    const stagedMethodTypes = {
      pending: [...input.pendingMethodTypes].sort((a, b) => a.id.localeCompare(b.id)),
      overrides: Object.keys(input.methodTypeOverrides)
        .sort()
        .map((id) => input.methodTypeOverrides[id]),
    };

    const stagedPipelines = {
      pending: [...input.pendingPipelines]
        .sort((a, b) => a.id.localeCompare(b.id))
        .map((pending) => this.toPlainStagedPipeline(pending)),
      overrides: Object.keys(input.pipelineOverrides)
        .sort()
        .map((id) => this.toPlainPipelineOverride(input.pipelineOverrides[id])),
    };

    const resources = {
      pending: input.pendingResources
        .map((rowData) => this.sanitizeLedgerRow(rowData))
        .sort((a, b) => a.id.localeCompare(b.id)),
      overrides: Object.keys(input.resourceOverrides)
        .sort()
        .map((id) => ({
          original: this.sanitizeLedgerRow(input.resourceOverrides[id].original),
          override: this.sanitizeLedgerRow(input.resourceOverrides[id].override),
        })),
    };

    const labels = {
      pending: [...input.pendingLabels].sort((a, b) => a.uuid.localeCompare(b.uuid)),
      overrides: Object.keys(input.labelOverrides)
        .sort()
        .map((uuid) => input.labelOverrides[uuid]),
      configs: [...input.stagedConfigs].sort((a, b) => a.stagingId.localeCompare(b.stagingId)),
    };

    return {
      row,
      fields: input.fields,
      assignedMethodTypeDeltas,
      stagedMethodTypes,
      stagedPipelines,
      resources,
      labels,
    };
  }

  /**
   * Staged drafts carry structured steps whose categories are
   * `PipelineStepCategory` class instances — the strict signature service
   * rejects them; flatten to plain objects (`PipelineEditorEffects.toPlainStep`
   * parity).
   */
  private toPlainStagedPipeline(pending: StagedPipelineDraft): unknown {
    return { ...pending, steps: pending.steps.map((step) => this.toPlainStep(step)) };
  }

  /** Manual drafts hold an opaque plain-JSON `params` blob and pass through as-is. */
  private toPlainStep(step: PipelineStepDraftDto): unknown {
    if (step.mode === 'manual') return { ...step };
    return {
      ...step,
      categories: step.categories.map((category) => ({
        uuid: category.uuid,
        name: { ...category.name },
        options: category.options.map((option) => ({
          uuid: option.uuid,
          name: { ...option.name },
          thrdis: option.thrdis,
        })),
      })),
    };
  }

  /**
   * `original` is the pre-edit DB summary; its `createdAt: Date` is dropped —
   * catalog metadata, constant within a session, never user work (and the
   * strict signature service rejects `Date`s).
   */
  private toPlainPipelineOverride(override: StagedPipelineOverride): unknown {
    const { original } = override;
    return {
      original: {
        id: original.id,
        name: original.name,
        env: original.env,
        usedByMethodTypes: original.usedByMethodTypes,
        numSteps: original.numSteps,
        pending: original.pending,
      },
      draft: this.toPlainStagedPipeline(override.draft),
    };
  }

  /*
   * `nestedResourcesCount` / `pending` are stripped — tree-derived bookkeeping
   * resynced on every tree emission; including them would flag phantom dirt.
   */
  private sanitizeLedgerRow(row: ResourceDraftData) {
    return {
      id: row.id,
      name: row.name,
      type: row.type,
      description: row.description,
      version: row.version,
      value: row.value,
      metadata: row.metadata ?? {},
      displayName: this.normalizeDisplayName(row.displayName),
      file: this.fileIdentity(row.file),
    };
  }

  /* A picked File compares by identity fields — the object itself is not signable. */
  private fileIdentity(file: File | null | undefined): { name: string; size: number; lastModified: number } | null {
    return file ? { name: file.name, size: file.size, lastModified: file.lastModified } : null;
  }

  /** Drops empty-string entries so `{}` ≡ `{en: ''}`; key sorting is the service's job. */
  private normalizeDisplayName(displayName: Record<string, string> | undefined): Record<string, string> {
    return Object.fromEntries(Object.entries(displayName ?? {}).filter(([, value]) => value !== ''));
  }
}
